<template>
    <div
      aria-controls="billing-collapse"
      data-bs-target="#billing-collapse"
      aria-expanded="false"
      class="resource-header-v2 d-flex align-items-center justify-content-between px-16 py-8 bg-white cursor-pointer rounded-sm mb-12"
      data-bs-toggle="collapse"
    >
      <div class="d-flex align-items-center gap-12">
        <div class="type-icon-box bg-dark text-white rounded p-4 d-flex align-items-center justify-content-center" style="width:28px;height:28px;">
          <v-icon icon="bookmark" size="sm" />
        </div>
        <h4 class="h6 fw-bold mb-0 text-uppercase letter-spacing-1">
          {{ $t("general.shared.billing") }}
        </h4>
      </div>
      <v-icon class="accordion-icon-v2" icon="chevron-up" size="sm" />
    </div>
    <div id="billing-collapse" class="billing-section accordion-body collapse p-16 w-100 bg-white border rounded-sm mb-24">
      <div class="d-flex justify-content-end gap-12 mb-16">
        <v-button
          class="btn-icon billing-action-btn"
          :class="invoiceAmount?.custom_notes ? 'btn-notes-active' : 'btn-notes-default'"
          size="xs"
          variant="icon"
          :disabled="isSold"
          @click="openCustomNotesModal"
        >
          <v-icon icon="comment" size="xs" />
        </v-button>
        <v-button
          v-if="!isDraft"
          size="sm"
          variant="primary"
          class="text-uppercase x-small fw-bold px-16 billing-action-btn btn-new-invoice"
          :disabled="isSold"
          @click="openBillingOffCanvas"
        >
          <v-icon icon="plus" size="xs" space="me-8" />
          {{ $t("tourPricing.newInvoice") }}
        </v-button>
      </div>
      <div class="row align-items-center pt-8 w-100">
        <div class="col-2">
          <p class="mb-0 small fw-bold text-uppercase">Total Program</p>
        </div>
        <div class="col-7 d-flex justify-content-end total-price-container">
          <v-pricing-update
            :id="`tour-${tourId}`"
            :gross="gross"
            :markup="markup"
            :net="net"
            :agency="agency"
            :case-id="Number(caseId)"
            :tour-id="Number(tourId)"
            class="is-mobile"
            is-total
            is-invoice
            is-new-version
            :is-sold="isSold"
            @update-total-pricing="onUpdateTotalPricing"
            @load-tour-pricing="onLoadTourPricing"
          />
        </div>
        <div class="col-3 quote-container d-flex align-items-center">
          <v-form-input
            autocomplete="off"
            form-type="input-group"
            id="quote-price"
            v-model="localQuotePrice"
            onwheel="return false;"
            right-addon="€"
            step=".01"
            type="number"
            :disabled="lock || isSold"
            @blur="emitUpdateQuotePrice('input')"
          />
          <div class="ms-2 lock-btn">
            <v-button
              class="btn p-2 text-white billing-action-btn btn-lock-quote"
              variant="icon"
              :disabled="isSold"
              @click="
                toggleLock();
                emitUpdateQuotePrice('button');
              "
            >
              <TheLock v-if="lock" style="color: white; width: 14px; height: 14px;" />
              <TheUnLock v-else style="color: white; width: 14px; height: 14px;" />
            </v-button>
          </div>
        </div>
      </div>
      <div
        class="row align-items-center pt-8 w-100"
        v-for="item in invoiceListData"
        :key="item.id"
      >
          <!-- Invoice Field -->
          <div class="col-2">
            <div class="button-container">
              <span class="button-label">{{ $t("general.shared.invoice") }}</span>
              <button class="custom-button py-1 px-2" :id="`invoice-${item.id}`" @click="navigateToInvoicePage(item.id)">
                <v-icon icon="pdf" size="xs" space="me-4" />
                <span class="small">{{ item.number }}</span>
              </button>
            </div>
          </div>
  
          <!-- Concept Field -->
          <div class="col-7 d-flex justify-content-end total-price-container">
            <v-form-input
              :id="`concept-${item.id}`"
              class="w-100 disabled-input"
              :label="$t('general.shared.concept')"
              autocomplete="off"
              form-type="outline"
              type="text"
              input-type="textarea"
              disabled
              v-model="item.invoice_lines[0].description"
            />
          </div>
  
          <!-- Amount Field -->
          <div class="col-3 quote-container d-flex align-items-center">
            <v-form-input
              :id="`amount-${item.id}`"
              :label="$t('general.shared.amount')"
              autocomplete="off"
              form-type="outline"
              type="text"
              disabled
              class="disabled-input"
              v-model="item.amount"
            />
          </div>
      </div>
      <!-- Remaining Amount -->
      <div class="row align-items-center pt-4 w-100">
        <div class="col-9 text-end"></div>
        <div class="col-3 quote-container d-flex align-items-center">
          <v-form-input
            id="remaining-amount"
            :label="$t('general.shared.remainingAmount')"
            autocomplete="off"
            form-type="outline"
            type="text"
            class="disabled-input"
            disabled
            v-model="tourRemainingAmount"
          />
        </div>
      </div>
      <template v-if="showFlightsInvoice">
        <hr class="my-12" />
        <div class="row align-items-center pt-8 w-100">
          <div class="col-2"><p class="mb-0 small fw-bold text-uppercase">Total Flight</p></div>
          <div class="col-7 d-flex justify-content-end total-price-container">
            <v-pricing-update
              :id="`tour-flight-${tourId}`"
              :gross="flightgrossPrice"
              :markup="flightmarkupPrice"
              :net="flightnetPrice"
              :agency="agency"
              :case-id="Number(caseId)"
              :tour-id="Number(tourId)"
              class="is-mobile"
              is-flight
              is-group-type
              is-invoice
              is-new-version
              :is-sold="isSold"
              @updated-total-type-pricing="onUpdatedTotalTypePricing($event, 'plane')"
              @update-total-pricing="onUpdateTotalPricing"
              @load-tour-pricing="onLoadTourPricing"
            />
          </div>
          <div class="col-3"></div>
        </div>
        <div
          class="row align-items-center pt-8 w-100"
          v-for="item in invoiceListData"
          :key="item.id"
        >
            <!-- Invoice Field -->
            <div class="col-2">
              <div class="button-container">
                <span class="button-label">{{ $t("general.shared.invoice") }}</span>
                <button class="custom-button py-1 px-2" :id="`invoice-${item.id}`" @click="navigateToInvoicePage(item.id)">
                  <v-icon icon="pdf" size="xs" space="me-4" />
                  <span class="small">{{ item.number }}</span>
                </button>
              </div>
            </div>
  
            <!-- Concept Field -->
            <div class="col-7 d-flex justify-content-end total-price-container">
              <v-form-input
                :id="`concept-${item.id}`"
                class="w-100 disabled-input"
                :label="$t('general.shared.concept')"
                autocomplete="off"
                form-type="outline"
                input-type="textarea"
                type="text"
                disabled
                v-model="item.description"
              />
            </div>
  
            <!-- Amount Field -->
            <div class="col-3 quote-container d-flex align-items-center">
              <v-form-input
                :id="`amount-${item.id}`"
                :label="$t('general.shared.amount')"
                autocomplete="off"
                form-type="outline"
                type="text"
                class="disabled-input"
                disabled
                v-model="item.amount"
              />
            </div>
        </div>
        <!-- Remaining Amount -->
        <div class="row align-items-center pt-4 mb-8 w-100">
          <div class="col-9 text-end"></div>
          <div class="col-3 quote-container d-flex align-items-center">
            <v-form-input
              id="remaining-amount"
              :label="$t('general.shared.remainingAmount')"
              autocomplete="off"
              form-type="outline"
              type="text"
              v-model="flightRemainingAmount"
              disabled
              class="disabled-input"
            />
          </div>
        </div>
      </template>
    </div>
    <the-custom-notes-off-canvas
      v-if="offCanvasToShow === 'the-custom-notes-off-canvas'"
      :agency="agency"
      :case-id="Number(caseId)"
      :tour-id="Number(tourId)"
      type="invoice"
      is-new-version
      :invoice-notes="!!invoiceAmount ? invoiceAmount.custom_notes : ''"
      @closed="closeOffCanvas()"
      @reload-items="onLoadTourPricing"
      @invoice-list="emitInvoiceList()"
    />
  </template>
  
  <script>
  import { VFormInput } from "@uniqoders/form";
  import VButton from "@/components/vendor/basic/button/VButton.vue";
  import VIcon from "@/components/vendor/basic/icon/VIcon.vue";
  import VPricingUpdate from "@/components/pricing/VPricingUpdate.vue";
  import TheLock from "@/assets/icons/TheLock.vue";
  import TheUnLock from "@/assets/icons/TheUnLock.vue";
  import { useUserStore } from "@/stores/user";
  import { mapState } from "pinia";
  import TheCustomNotesOffCanvas from "@/components/notes/TheCustomNotesOffCanvas.vue";
  import useOffCanvasUtils from "@/helpers/OffCanvasUtils";
  
  export default {
    name: "TheCasePricingBilling",
    components: {
      VPricingUpdate,
      VFormInput,
      VButton,
      VIcon,
      TheLock,
      TheUnLock,
      TheCustomNotesOffCanvas,
    },
    emits: [
      "loadTourPricing",
      "updateTotalPricing",
      "update:gross",
      "update:markup",
      "update:net",
      "update:quote",
      "updateQuotePrice",
      "updatedTotalTypePricing",
      "toggleLock",
      "openOffCanvas",
      "invoiceList",
    ],
    setup() {
      return {
        ...useOffCanvasUtils(),
      };
    },
    props: {
      agency: {
        type: String,
        required: true,
      },
      caseId: {
        type: Number,
        required: true,
      },
      tourId: {
        type: Number,
        required: true,
      },
      isTotal: {
        type: Boolean,
        required: false,
        default: false,
      },
      net: {
        type: Number,
        required: true,
      },
      markup: {
        type: Number,
        required: true,
      },
      gross: {
        type: Number,
        required: true,
      },
      quote: {
        type: Number,
        required: true,
      },
      lock: {
        type: Boolean,
        required: false,
        default: false,
      },
      invoiceListData: {
        type: Array,
        required: false,
        default: () => [],
      },
      invoiceAmount: {
        type: Object,
        required: false,
        default: () => null,
      },
      isNewVersion: {
        type: Boolean,
        required: false,
        default: false,
      },
      isDraft: {
        type: Boolean,
        required: false,
        default: false,
      },
      isSold: {
        type: Boolean,
        required: false,
        default: false,
      }
    },
    data() {
      return {
        localQuotePrice: this.quote,
        tourRemainingAmount: this.invoiceAmount?.tour?.total_remaining,
        flightgrossPrice: this.invoiceAmount?.flights?.flights_gross,
        flightnetPrice: this.invoiceAmount?.flights?.flights_net,
        flightmarkupPrice: this.invoiceAmount?.flights?.flights_markup,
        flightRemainingAmount: this.invoiceAmount?.flights?.total_remaining,
        openedWindowPreview: null,
      };
    },
    watch: {
      quote(newValue) {
        this.localQuotePrice = newValue;
      },
      invoiceAmount(newValue) {
        this.tourRemainingAmount = newValue?.tour?.total_remaining;
        this.flightgrossPrice = newValue?.flights?.flights_gross;
        this.flightnetPrice = newValue?.flights?.flights_net;
        this.flightmarkupPrice = newValue?.flights?.flights_markup;
        this.flightRemainingAmount = newValue?.flights?.total_remaining;
      },
    },
    computed: {
      ...mapState(useUserStore, ["currentTour"]),
      showFlightsInvoice() {
        return this.invoiceListData?.filter((e) => e.type === "emitted_flight").length > 0;
      },
    },
    methods: {
      onUpdatedTotalTypePricing(response, type) {
        this.$emit("updatedTotalTypePricing", response, type);
        this.$emit("invoiceList");
      },
      onUpdateTotalPricing(data) {
        this.$emit("updateTotalPricing", data);
      },
      onLoadTourPricing() {
        this.$emit("loadTourPricing");
      },
      emitUpdateQuotePrice(source) {
        this.$emit("update:quote", this.localQuotePrice);
        this.$emit("updateQuotePrice", source);
        this.$emit("invoiceList");
      },
      toggleLock() {
        this.$emit("toggleLock");
      },
      openBillingOffCanvas() {
        this.$emit("openOffCanvas");
      },
      openCustomNotesModal() {
        this.openOffCanvas("the-custom-notes-off-canvas");
      },
      emitInvoiceList() {
        this.$emit("invoiceList");
      },
      navigateToInvoicePage(invoiceNumber) {
        if (!!this.openedWindowPreview && !this.openedWindowPreview.closed) {
          this.openedWindowPreview.close();
        }
        const routeData = this.$router.resolve({
          name: "case.invoice.invoice",
          params: { caseId: this.caseId, invoice: invoiceNumber },
          query: {
            token: this.currentTour?.token,
            locale: !!localStorage.getItem("language")
              ? localStorage.getItem("language")
              : !!import.meta.env.VITE_APP_DEFAULT_LOCALE
              ? import.meta.env.VITE_APP_DEFAULT_LOCALE
              : !!import.meta.env.VITE_APP_DEFAULT_LOCALE
              ? import.meta.env.VITE_APP_DEFAULT_LOCALE
              : "en",
            summary: 1,
            agency: this.agency,
          },
        });
        const width = 1100;
        const height = 800;
        const top = window.top.outerHeight / 2 + window.top.screenY - height / 2;
        const left = window.top.outerWidth / 2 + window.top.screenX - width / 2;
  
        this.openedWindowPreview = window.open(
          routeData.href,
          "_blank",
          `scrollbars=no,resizable=no,status=no,location=no,toolbar=no,menubar=no,left=${left},width=${width},height=${height},top=${top}`
        );
      },
    },
  };
  </script>
  
  <style scoped lang="scss">
  .resource-header {
    cursor: pointer;
    margin-bottom: 24px;
  }

  .resource-header-v2 {
    transition: all 0.2s ease;
    &:hover {
      border-color: var(--uq-primary) !important;
    }
    
    &[aria-expanded="true"] {
      .accordion-icon-v2 {
        transform: rotate(180deg);
      }
    }
  }

  .accordion-icon-v2 {
    transition: transform 0.3s ease;
  }

  .type-icon-box {
    width: 32px;
    height: 32px;
    background-color: #fd0c6f !important;
    border-radius: 6px !important;
  }

  .x-small { font-size: 0.7rem; }
  .letter-spacing-1 { letter-spacing: 1px; }

  /* Global label refinements for ultra-compact look */
  :deep(label),
  :deep(.v-form-label),
  :deep(.form-check-label) {
    font-size: 0.6rem !important;
    font-weight: 700 !important;
    text-transform: uppercase !important;
    letter-spacing: 0.5px !important;
    margin-bottom: 2px !important;
    color: #94a3b8 !important;
  }
  
  .billing-section {
    border: 1px solid var(--uq-gray-400);
    border-radius: 0.25rem;
  
    &:not(:last-child) {
      margin-bottom: 1rem;
    }
  
    .total-price-container {
      .is-mobile {
        width: 100% !important;
      }
    }
  
    .quote-container {
      display: flex;
      align-items: center;
  
      .lock-btn {
        margin-left: 0.5rem;
        align-self: center;
      }
  
      @media screen and (max-width: 992px) {
        flex-direction: column;
        align-items: flex-start;
  
        .lock-btn {
          margin-top: 8px;
          align-self: flex-end;
        }
      }
    }
  
    .disabled-input {
      :deep(.form-control) {
        background-color: #f7f7fc !important;
        border: 0.0625rem solid #d5d5d5 !important;
        color: black;
        line-height: 25px !important;
      }
    }
  }
  
  .custom-button {
    display: flex;
    align-items: center;
    justify-content: start;
    width: 100%;
    font-size: 16px;
    background-color: #fff8fb;
    border: 1px solid rgba(253, 12, 111, 0.25);
    border-radius: 8px;
    color: #222422;
    text-align: left;
    border-radius: 6px;
    padding: 0.45rem;
    min-height: 3.0625rem;
    transition: all 0.2s ease;
  }
  .custom-button:hover {
    background-color: rgba(253, 12, 111, 0.1);
    border-color: rgba(253, 12, 111, 0.45);
  }
  .button-label {
    color: #666;
    position: absolute;
    transform: translateY(-12px);
    background: white;
    padding: 0 5px;
    margin-left: 16px;
  }
  .button-container {
    position: relative;
    display: inline-block;
    width: 100%;
    padding-top: 20px;
  }
  
  .cutom-notes {
    background-color: #32de84;
    border-color: #32de84;
  }
  
  .cutom-notes:hover {
    background-color: #32de84;
    border-color: #32de84;
  }

  .billing-action-btn {
    border-radius: 6px !important;
  }

  .btn-notes-default {
    background-color: #f09d57 !important;
    border-color: #f09d57 !important;
    color: #222422 !important;
  }

  .btn-notes-default:hover {
    background-color: #e58f45 !important;
    border-color: #e58f45 !important;
  }

  .btn-notes-active {
    background-color: #57c68a !important;
    border-color: #57c68a !important;
    color: #ffffff !important;
  }

  .btn-notes-active:hover {
    background-color: #45b478 !important;
    border-color: #45b478 !important;
  }

  .btn-new-invoice {
    background-color: #fd0c6f !important;
    border-color: #fd0c6f !important;
    color: #ffffff !important;
  }

  .btn-new-invoice:hover {
    background-color: #dc095f !important;
    border-color: #dc095f !important;
  }

  .btn-lock-quote {
    background-color: #222422 !important;
    border-color: #222422 !important;
  }

  .btn-lock-quote:hover {
    background-color: #171917 !important;
    border-color: #171917 !important;
  }
  </style>
  